import React, { useEffect, useState } from "react";
import logger from "../lib/logger";
import { motion } from "framer-motion";
import { Btn, Eyebrow } from "../components/Primitives";
import { api } from "../lib/api";

const CARD_RISE_INIT = { opacity: 0, y: 8 };
const CARD_RISE_ANIM = { opacity: 1, y: 0 };

function useCountdown(targetIso) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60_000);
        return () => clearInterval(id);
    }, []);  
    if (!targetIso) return null;
    const diff = new Date(targetIso).getTime() - now;
    if (diff <= 0) return "Refreshing soon";
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
}

export default function WeeklyPicks({ user, onBack, onOpenProfile, onOpenConcierge }) {
    const [data, setData] = useState({ items: [], max_per_week: 3, next_refresh_at: null });
    const [loading, setLoading] = useState(true);
    const countdown = useCountdown(data.next_refresh_at);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.weeklyPicks(user.session_id);
                setData(r);
            } catch (err) {
                logger.error("weeklyPicks load failed:", err);
            }
            setLoading(false);
        })();
    }, [user.session_id]);

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="weekly-picks-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button data-testid="picks-back-btn" onClick={onBack} className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted">
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Your week</span>
            </div>

            <div className="px-7 pt-4 pb-2">
                <Eyebrow>This week's picks</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-[1.1]">
                    <span className="italic">Three</span> people, hand-picked.
                </h1>
                <p className="mt-2 text-sm text-ink-muted leading-snug">
                    Volume is the enemy. Each week we send you three of the most compatible profiles — no swiping, no doom-scrolling. New picks every Monday.
                </p>
                <div className="mt-3 flex items-center gap-2">
                    <span className="text-[10px] tracking-[0.32em] uppercase text-gold-deep" data-testid="picks-countdown">
                        Next refresh in {countdown || "—"}
                    </span>
                </div>
            </div>

            <div className="flex-1 px-6 py-5 space-y-3 overflow-y-auto">
                {loading && <div className="text-ink-muted text-sm text-center py-10">Loading…</div>}

                {!loading && data.items.length === 0 && (
                    <div className="text-center py-12 px-4" data-testid="picks-empty">
                        <p className="font-display italic text-xl text-burgundy">No more picks this week.</p>
                        <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                            You've seen everyone we have for you. Come back Monday for three new compatible people.
                        </p>
                        {onOpenConcierge && (
                            <button
                                data-testid="picks-empty-concierge-btn"
                                onClick={onOpenConcierge}
                                className="mt-6 inline-block text-[11px] tracking-[0.28em] uppercase text-burgundy underline underline-offset-4"
                            >
                                Ask the concierge for date ideas →
                            </button>
                        )}
                    </div>
                )}

                {!loading && data.items.map((m, i) => (
                    <motion.button
                        key={m.public_id}
                        initial={CARD_RISE_INIT}
                        animate={CARD_RISE_ANIM}
                        transition={{ delay: i * 0.06 }}
                        onClick={() => onOpenProfile(m.public_id)}
                        data-testid={`weekly-pick-card-${m.public_id}`}
                        className="w-full text-left flex gap-4 bg-cream border border-burgundy/15 hover:border-burgundy/40 transition-colors p-3 items-center"
                    >
                        <div className="w-20 h-24 bg-burgundy/5 overflow-hidden border border-burgundy/10 shrink-0 relative">
                            {m.photo_url ? (
                                <img src={m.photo_url} alt={m.first_name} className="w-full h-full object-cover" />
                            ) : null}
                            <div className="absolute top-1 left-1 bg-burgundy text-cream text-[8px] tracking-[0.24em] uppercase px-1 py-0.5">
                                #{i + 1}
                            </div>
                        </div>
                        <div className="flex-1 min-w-0">
                            {m.is_archetype_match && (
                                <span className="text-[9px] tracking-[0.32em] uppercase bg-gold/30 text-burgundy px-1.5 py-0.5">Archetype match</span>
                            )}
                            <div className="font-display text-xl text-burgundy mt-1">
                                {m.first_name}, {m.age}
                            </div>
                            <div className="text-xs text-ink-muted">{m.city}</div>
                            <p className="mt-1 text-xs text-ink leading-snug line-clamp-2">{m.bio}</p>
                        </div>
                    </motion.button>
                ))}

                {!loading && data.items.length > 0 && data.items.length < data.max_per_week && (
                    <div className="mt-4 text-center text-[10px] tracking-[0.32em] uppercase text-ink-light" data-testid="picks-partial">
                        {data.items.length} of {data.max_per_week} this week
                    </div>
                )}
            </div>

            {!loading && onOpenConcierge && (
                <div className="px-6 pb-8 pt-2 pb-safe">
                    <Btn data-testid="picks-concierge-btn" variant="secondary" onClick={onOpenConcierge}>
                        Ask the concierge to draft an opener
                    </Btn>
                </div>
            )}
        </div>
    );
}
